import { Card, Col, Row, Spin, Tag } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import HeaderDetailComponent from "../../components/header/headerDetailComponent";
import { useLocation, useNavigate } from "react-router-dom";
import { MEMBER_URL } from "../../api/URLs";
import { getDataRequest } from "../../api/serviceMethods";
import { formatDateUTC, formatDateTime } from "../../utils/dateFormatUtils";
import { getSubscriptionPlanLabelKey } from "../../utils/memberStatusUtils";

// Title of each history entry (membership_histories.action)
const ACTION_LABELS = {
    trial_started: "free_trial_started",
    purchase_activate: "purchased_new",
    purchase_renewal: "purchased_renewal",
    trial_ended: "free_trial_ended",
    admin_add_pro: "admin_added_pro",
    admin_change_end: "admin_changed_end_date",
};

const showDate = (date) => (date ? formatDateUTC(date) : "N/A");

/** "old → new" when the value changed, otherwise just the value */
const showChange = (before, after) => {
    if (before && after && formatDateUTC(before) !== formatDateUTC(after)) {
        return `${formatDateUTC(before)} → ${formatDateUTC(after)}`;
    }
    return showDate(after || before);
};

const getEntryDates = (entry) => {
    const { action, before, after } = entry;

    if (action === "admin_add_pro" || action === "purchase_activate" || action === "trial_started") {
        return {
            start: showDate(after?.startTS),
            end: showDate(after?.endTS),
        };
    }

    if (action === "purchase_renewal") {
        return {
            start: showDate(before?.endTS || after?.startTS),
            end: showDate(after?.endTS),
        };
    }

    return {
        start: showChange(before?.startTS, after?.startTS),
        end: showChange(before?.endTS, after?.endTS),
    };
};

const PaymentHistory = () => {
    const [loading, setLoading] = useState(false);
    const [entries, setEntries] = useState([]);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const userDetailId = params.get("id");

    const fetchHistory = async () => {
        try {
            setLoading(true);
            const response = await getDataRequest(`${MEMBER_URL}/history/${userDetailId}`);
            if (response.status === 200) {
                setEntries(response.data || []);
            }
        } catch (err) {
            console.error("Error fetching membership history:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userDetailId) {
            fetchHistory();
        }
    }, [userDetailId]);

    const operatorName = (operator) => {
        if (operator?.kind === "staff") {
            const staff = operator.staffId;
            return (staff && (staff.name || staff.account || staff.email)) || t("operator_staff");
        }
        if (operator?.kind === "member") return t("operator_member");
        return t("operator_system");
    };

    const renderRow = (label, value, className = "mt-3") => (
        <Row align="middle" className={className}>
            <Col span={8} className="userLabel">{label}:</Col>
            <Col>{value}</Col>
        </Row>
    );

    return (
        <HeaderDetailComponent
            backgroundColor="#f8e2e8"
            border="1px solid #e72158"
            isHide="view"
            onClick={() => navigate(-1)}
            backTitle={t("Member History")}
            hideTitle="userDetails"
        >
            <div className="container newDetail">
                <Spin size="large" tip={t("loading")} spinning={loading}>
                    <div className="w-100 h-100" style={{ padding: "1rem 2rem" }}>
                        {entries.length === 0 && !loading ? (
                            <div className="text-center py-5">{t("no_history_yet")}</div>
                        ) : null}
                        {entries.map((entry) => {
                            const dates = getEntryDates(entry);
                            return (
                                <div key={entry._id} className="row gx-0 d-flex justify-content-center align-items-center">
                                    <div className="col-12 d-flex justify-content-between">
                                        <div className="d-flex justify-content-center align-items-center">
                                            <IconCircle />
                                            <Tag
                                                icon={<ClockCircleOutlined />}
                                                style={{
                                                    marginLeft: "0.75rem",
                                                    marginRight: 0,
                                                    padding: "3px 12px",
                                                    borderRadius: "999px",
                                                    backgroundColor: "#FFF0F4",
                                                    border: "1px solid #F8C6D4",
                                                    color: "#E50A47",
                                                    fontSize: "13px",
                                                    fontWeight: 600,
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "4px",
                                                }}
                                            >
                                                {formatDateTime(entry.occurredTS)}
                                            </Tag>
                                        </div>
                                    </div>
                                    <div className="col-12 d-flex justify-content-center align-items-center ps-1">
                                        <div
                                            className="w-100 h-100"
                                            style={{
                                                paddingLeft: "1.5rem",
                                                paddingTop: "0.5rem",
                                                paddingBottom: "1.5rem",
                                                borderLeft: "0.125rem dashed #F8E2E8",
                                            }}
                                        >
                                            <Card style={{ width: "100%" }}>
                                                <p className="userTitle">{t(ACTION_LABELS[entry.action] || entry.action)}</p>
                                                {renderRow(t("Membership Type"), t(getSubscriptionPlanLabelKey(entry.plan)), "")}
                                                {renderRow(t("Start Time"), dates.start)}
                                                {renderRow(t("End Time"), dates.end)}
                                                {entry.reason ? renderRow(t("Remark"), entry.reason) : null}
                                                {renderRow(t("operated_by"), operatorName(entry.operator))}

                                                {/* <div className="mt-4 pt-3" style={{ borderTop: "1px dashed #e8e8e8" }}>
                                                <div className="userLabel mb-2" style={{ fontWeight: 600 }}>
                                                    {t("result")}:
                                                </div>
                                                <div className="p-2" style={{ background: "#fafafa", borderRadius: "6px" }}>
                                                    {renderRow(t("free_trial_end"), showDate(entry.planEnds?.free), "")}
                                                    {renderRow(t("normal_membership_end"), showDate(entry.planEnds?.pro), "mt-2")}
                                                    {renderRow(t("super_membership_end"), showDate(entry.planEnds?.super), "mt-2")}
                                                    {renderRow(
                                                        <strong>{t("membership_end")}</strong>,
                                                        <strong>{showDate(entry.membershipEndTS)}</strong>,
                                                        "mt-2"
                                                    )}
                                                </div>
                                            </div> */}
                                            </Card>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Spin>
            </div>
        </HeaderDetailComponent>
    );
};

const IconCircle = () => (
    <div
        style={{
            width: "0.65rem",
            height: "0.65rem",
            borderRadius: "100%",
            background: "#E50A47",
            boxShadow: "0 0 0 4px #F8E2E8",
            flexShrink: 0,
        }}
    />
);

export default PaymentHistory;
